import {
  Listbox,
  Transition,
  Combobox,
  ListboxOptions,
  ListboxButton,
  ListboxOption,
  ComboboxButton,
  ComboboxInput,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/react';
import { Float } from '@headlessui-float/react';
import { Fragment, useState, useRef, useEffect } from 'react';
import { FaChevronDown } from 'react-icons/fa';
import { RiSearchLine } from 'react-icons/ri';

import InputError from './InputError';
import Label from './Label';

import { Maybe, SelectValue } from '@/types';
import { cn, TEXTS } from '@/utils';

interface DropdownProps {
  label?: string;
  options: SelectValue[];
  selected: Maybe<SelectValue>;
  onChange: (option: SelectValue) => void;
  onBlur?: () => void;
  error?: string;
  filterable?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  withFloat?: boolean;
  placement?: 'top' | 'bottom';
  withSearchIcon?: boolean;
  onKeyPress?: (value: string) => void;
  onOpenStateChange?: (isOpen: boolean) => void;
  showArrowIcon?: boolean;
  optionsClassName?: string;
}

const DropdownIcons = ({
  error,
  open,
  disabled,
  showArrowIcon = true,
}: {
  error?: string;
  open: boolean;
  disabled?: boolean;
  showArrowIcon?: boolean;
}) => {
  return (
    <span
      className={cn(
        'pointer-events-none absolute inset-y-0 flex items-center',
        {
          'right-[16px]': !error,
          'right-[0px]': error,
          '!right-[16px]': open,
        }
      )}
    >
      {error && !open ? (
        <InputError.Icons error={error} />
      ) : (
        showArrowIcon && (
          <FaChevronDown
            className={cn('text-gdt-blue w-[10px] transition duration-200', {
              'rotate-180': open,
              'text-gdt-gray-4': disabled,
            })}
          />
        )
      )}
    </span>
  );
};

const DISABLED_CLASS =
  'border-gdt-gray-5 bg-gdt-gray-6 focus-visible:border-gdt-gray-5 hover:border-gdt-gray-5';

const Dropdown = ({
  label,
  options,
  selected,
  error,
  filterable = false,
  placeholder = 'Seleccionar',
  disabled,
  onChange,
  onBlur,
  className,
  withFloat,
  placement: direction,
  withSearchIcon,
  onKeyPress,
  onOpenStateChange,
  showArrowIcon = true,
  optionsClassName,
}: DropdownProps) => {
  const [query, setQuery] = useState('');
  const comboBtn = useRef<HTMLButtonElement>(null);
  const onSearchInputFocus = () => comboBtn.current?.click();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [openState, setOpenState] = useState(false);

  useEffect(() => {
    onOpenStateChange?.(openState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openState]);

  useEffect(() => {
    const value = query ?? '';
    onKeyPress && value !== '' && onKeyPress(query.trim());
  }, [query, onKeyPress]);

  const filteredPeople =
    query === '' || onKeyPress
      ? options
      : options.filter((person) => {
          return person.label.toLowerCase().includes(query.toLowerCase());
        });

  if (filterable) {
    return (
      <fieldset className="space-y-1">
        <Combobox disabled={disabled} value={selected} onChange={onChange}>
          {({ open }) => {
            return (
              <div className="space-y-[10px]">
                {label && <Label>{label}</Label>}
                <div className="relative">
                  <div className="relative">
                    <ComboboxButton ref={comboBtn} className="hidden" />
                    {withSearchIcon && (
                      <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 z-10">
                        <RiSearchLine
                          className={cn('text-gdt-blue w-5 h-5', {
                            'opacity-20': disabled,
                          })}
                        />
                      </div>
                    )}
                    <ComboboxInput
                      className={cn(
                        'relative font-medium w-full truncate focus-visible:truncate bg-white py-[12px] pl-[16px] pr-[30px] text-left text-gdt-gray-2 cursor-pointer focus-visible:outline-none border border-gdt-gray-5 rounded-[8px] focus-visible:border-gdt-blue hover:border-gdt-blue text-sm focus:outline-none focus:ring-0',
                        {
                          '!border-gdt-blue !bg-white': open,
                          'border-gdt-status-red': error,
                          [`${DISABLED_CLASS} text-gdt-gray-4 cursor-default`]:
                            disabled,
                          'pl-10': withSearchIcon,
                        }
                      )}
                      displayValue={(person: any) => person?.label}
                      placeholder={placeholder}
                      onBlur={onBlur}
                      onChange={(event) => setQuery(event.target.value)}
                      onClick={onSearchInputFocus}
                    />
                    <DropdownIcons
                      disabled={disabled}
                      error={error}
                      open={open}
                      showArrowIcon={showArrowIcon}
                    />
                  </div>
                  <Transition
                    afterLeave={() => setQuery('')}
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <ComboboxOptions
                      className={cn(
                        'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 divide-y divide-gdt-gray-5 shadow-md focus-visible:outline-none',
                        optionsClassName
                      )}
                      modal={false}
                    >
                      {filteredPeople.length === 0 && query !== '' ? (
                        <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                          {TEXTS.noResults}
                        </div>
                      ) : (
                        filteredPeople.map((person) => (
                          <ComboboxOption
                            key={person.value}
                            className={({ active, selected }) => {
                              return cn(
                                'relative cursor-pointer select-none py-[12px] px-[16px]',
                                {
                                  'bg-gdt-blue': active || selected,
                                  'text-gdt-gray-2': !active,
                                }
                              );
                            }}
                            value={person}
                          >
                            {({ selected, active }) => (
                              <>
                                <span
                                  className={cn(
                                    'block truncate text-sm text-gdt-gray-2 font-medium',
                                    {
                                      'text-white': active || selected,
                                    }
                                  )}
                                >
                                  {person.label}
                                </span>
                              </>
                            )}
                          </ComboboxOption>
                        ))
                      )}
                    </ComboboxOptions>
                  </Transition>
                </div>
              </div>
            );
          }}
        </Combobox>
        {error && <InputError.Label>{error}</InputError.Label>}
      </fieldset>
    );
  }

  return (
    <fieldset className="space-y-1">
      <Listbox disabled={disabled} value={selected} onChange={onChange}>
        {({ open }) => {
          if (open !== openState) {
            setOpenState(open);
          }

          return (
            <div className="space-y-[10px]">
              {label && <Label>{label}</Label>}
              <div className={`relative ${className}`}>
                <Float
                  as="div"
                  className="relative "
                  floatingAs={Fragment}
                  placement={direction}
                  portal={withFloat}
                >
                  <ListboxButton
                    ref={buttonRef}
                    className={cn(
                      'grid relative font-medium w-full bg-white py-[12px] pl-[16px] pr-[30px] text-left text-gdt-gray-4 cursor-pointer focus-visible:outline-none border border-gdt-gray-5 rounded-[8px] focus-visible:border-gdt-blue hover:border-gdt-blue',
                      {
                        '!border-gdt-blue !bg-white': open,
                        'border-gdt-status-red': error,
                        [`${DISABLED_CLASS} cursor-default`]: disabled,
                      }
                    )}
                    onBlur={onBlur}
                  >
                    <span className="block truncate w-[97%]">
                      {selected ? (
                        <u
                          className={cn('text-gdt-gray-2 no-underline', {
                            'text-gdt-gray-4': disabled,
                          })}
                        >
                          {selected.label}
                        </u>
                      ) : (
                        placeholder
                      )}
                    </span>
                    <DropdownIcons
                      disabled={disabled}
                      error={error}
                      open={open}
                      showArrowIcon={showArrowIcon}
                    />
                  </ListboxButton>

                  <Transition
                    as={Fragment}
                    leave="transition ease-in duration-100"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    show={open}
                  >
                    <ListboxOptions
                      ref={dropdownRef}
                      className={cn(
                        `absolute z-10 mt-1 max-h-60 overflow-auto rounded-md bg-white py-1 divide-y divide-gdt-gray-5 shadow-md focus-visible:outline-none ${
                          withFloat ? 'w-96' : 'w-full'
                        } `,
                        optionsClassName
                      )}
                      modal={false}
                    >
                      {options.map((option) => (
                        <ListboxOption
                          key={option.value}
                          className={({ active, selected }) => {
                            return cn(
                              'relative cursor-pointer select-none py-[12px] px-[16px] ',
                              {
                                'bg-gdt-blue': active || selected,
                                'text-gdt-gray-2': !active,
                                'text-gdt-gray-4 bg-gdt-gray-5 cursor-not-allowed':
                                  option.disabled,
                              }
                            );
                          }}
                          disabled={option.disabled}
                          value={option}
                        >
                          {({ active, selected }) => (
                            <>
                              <span
                                className={cn(
                                  'block truncate text-sm text-gdt-gray-2 font-medium',
                                  {
                                    'text-white': active || selected,
                                    'text-gdt-gray-4 bg-gdt-gray-5 cursor-not-allowed':
                                      option.disabled,
                                  }
                                )}
                              >
                                {option.label}
                              </span>
                            </>
                          )}
                        </ListboxOption>
                      ))}
                    </ListboxOptions>
                  </Transition>
                </Float>
              </div>
            </div>
          );
        }}
      </Listbox>
      {error && <InputError.Label>{error}</InputError.Label>}
    </fieldset>
  );
};

export default Dropdown;
